import { Component, signal, inject, OnInit, OnDestroy, computed } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, transition, style, animate, query } from '@angular/animations';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { SidebarComponent } from './sidebar.component';
import { TopbarComponent } from './topbar.component';
import { AuthService } from '../auth/auth.service';
import { findActiveGroup } from './nav-config';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [RouterOutlet, CommonModule, FormsModule, SidebarComponent, TopbarComponent, DialogModule, ButtonModule, InputTextModule, ToastModule],
  providers: [MessageService],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.css',
  animations: [
    trigger('routeAnimation', [
      transition(':enter', [
        query(':enter', [
          style({ opacity: 0, transform: 'translateY(12px)' }),
          animate('0.4s cubic-bezier(0.22, 1, 0.36, 1)', style({ opacity: 1, transform: 'translateY(0)' })),
        ], { optional: true }),
      ]),
    ]),
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private routerSub!: Subscription;

  currentUrl = signal(this.router.url);
  mobileOpen = signal(false);
  sidebarCollapsed = signal(false);
  showDisplayNameDialog = false;
  displayNameInput = '';
  savingDisplayName = signal(false);
  displayNameError = '';
  isFemale = false;

  isHub = computed(() => {
    const url = this.currentUrl();
    return url === '/dashboard' || url === '/' || url === '/profile';
  });

  hasSidebar = computed(() => {
    return findActiveGroup(this.currentUrl()) !== null;
  });

  private static readonly FEMALE_NAMES = new Set([
    'maria','ana','rosa','carmen','julia','lucia','sofia','elena','laura','diana',
    'andrea','claudia','patricia','sandra','monica','gabriela','carolina','daniela',
    'valentina','fernanda','alejandra','natalia','camila','mariana','paola','silvia',
    'susana','teresa','victoria','veronica','yolanda','cecilia','norma','gladys',
    'elizabeth','milagros','katherine','jessica','jennifer','vanessa','karina',
    'marisol','luz','flor','rocio','pilar','martha','nancy','gloria','isabel',
    'betty','ruth','miriam','olga','irma','edith','sonia','dora','lilia','nelly',
    'hilda','ines','eva','sara','raquel','angela','blanca','elsa','lidia','esther',
    'doris','magaly','magali','jackeline','jacqueline','roxana','gisela','carla',
    'lorena','ivonne','yvonne','fiorella','miluska','medalit','yanina','yesenia',
    'lesly','leslie','wendy','karen','kelly','shirley','melisa','melissa','johana',
    'juana','josefa','guadalupe','esperanza','consuelo','aurora','bertha','delia',
  ]);

  ngOnInit(): void {
    this.routerSub = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(e => {
      this.currentUrl.set(e.urlAfterRedirects);
    });

    // Detecta cambio de contraseña obligatorio o sesión cerrada en Minerva al entrar
    this.auth.checkMinervaSession();

    const user = this.auth.user();
    if (user && !user.display_name && user.is_agency && user.agency_name) {
      this.auth.updateDisplayName(user.agency_name).subscribe({
        next: () => this.auth.saveDisplayNameLocally(user.agency_name!),
      });
    } else if (this.auth.needsDisplayName()) {
      this.displayNameInput = this.auth.suggestDisplayName();
      this.isFemale = this.detectFemale();
      this.showDisplayNameDialog = true;
    }
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  private detectFemale(): boolean {
    const user = this.auth.user();
    if (!user?.user_firstname) return false;
    const firstName = user.user_firstname.trim().split(/\s+/)[0].toLowerCase();
    if (LayoutComponent.FEMALE_NAMES.has(firstName)) return true;
    const maleEndingA = new Set(['joshua','borja','garcia','garcia','nikita']);
    if (firstName.endsWith('a') && !maleEndingA.has(firstName)) return true;
    return false;
  }

  confirmDisplayName(): void {
    const name = this.displayNameInput.trim();
    if (!name) return;
    this.displayNameError = '';
    this.savingDisplayName.set(true);
    this.auth.updateDisplayName(name).subscribe({
      next: () => {
        this.auth.saveDisplayNameLocally(name);
        this.savingDisplayName.set(false);
        this.showDisplayNameDialog = false;
        this.messageService.add({ severity: 'success', summary: 'Listo', detail: `Bienvenido, ${name}` });
      },
      error: (err) => {
        this.savingDisplayName.set(false);
        this.displayNameError = err?.error?.message || 'No se pudo guardar. Intenta de nuevo.';
      },
    });
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update(v => !v);
  }

  onMenuToggle(): void {
    if (window.innerWidth <= 991) {
      this.mobileOpen.update(v => !v);
    } else {
      this.sidebarCollapsed.update(v => !v);
    }
  }
}
